import { parse, TYPE, type MessageFormatElement } from '@formatjs/icu-messageformat-parser';
import { Array as A, Either, Order, pipe } from 'effect';

/*
 * Placeholder + tag signature of an ICU message. Two messages are compatible when both sets are equal. The overrides
 * service applies the same rule on save (its own copy — it is a separate package); this one is the runtime guard.
 */
export const MAX_OVERRIDE_LENGTH = 2000;

// U+200B–U+200D, U+2060–U+2064, U+FEFF: the marker alphabet and its neighbours.
export const INVISIBLE_CHARS_RE = /[\u200B-\u200D\u2060-\u2064\uFEFF]/;

export interface MessageSignature {
  readonly args: ReadonlyArray<string>;
  readonly tags: ReadonlyArray<string>;
}

interface Collected {
  readonly args: ReadonlyArray<string>;
  readonly tags: ReadonlyArray<string>;
}

const EMPTY: Collected = { args: [], tags: [] };

const concat = (a: Collected, b: Collected): Collected => ({
  args: [...a.args, ...b.args],
  tags: [...a.tags, ...b.tags],
});

const collect = (elements: ReadonlyArray<MessageFormatElement>): Collected =>
  elements.reduce<Collected>((acc, el) => {
    switch (el.type) {
      case TYPE.argument:
      case TYPE.number:
      case TYPE.date:
      case TYPE.time:
        return concat(acc, { args: [el.value], tags: [] });
      case TYPE.plural:
      case TYPE.select:
        return Object.values(el.options).reduce(
          (inner, option) => concat(inner, collect(option.value)),
          concat(acc, { args: [el.value], tags: [] }),
        );
      case TYPE.tag:
        return concat(concat(acc, { args: [], tags: [el.value] }), collect(el.children));
      default:
        return acc;
    }
  }, EMPTY);

const normalize = (xs: ReadonlyArray<string>) => pipe(A.dedupe(xs), A.sort(Order.string));

/** The signature, or the parser's error message when the text is not a valid ICU message. */
export const messageSignature = (message: string): Either.Either<MessageSignature, string> =>
  Either.try({
    try: () => {
      const { args, tags } = collect(parse(message));
      return { args: normalize(args), tags: normalize(tags) };
    },
    catch: (e) => (e instanceof Error ? e.message : String(e)),
  });

const sameSet = (a: ReadonlyArray<string>, b: ReadonlyArray<string>) =>
  a.length === b.length && a.every((x, i) => x === b[i]);

export const signaturesEqual = (a: MessageSignature, b: MessageSignature): boolean =>
  sameSet(a.args, b.args) && sameSet(a.tags, b.tags);

export const isPlainSignature = (s: MessageSignature): boolean => s.args.length === 0 && s.tags.length === 0;

/** True when `override` may replace `base` on screen: it parses, has no marker chars and the same signature. */
export const isCompatibleOverride = (base: string, override: string): boolean =>
  !INVISIBLE_CHARS_RE.test(override) &&
  pipe(
    Either.all([messageSignature(base), messageSignature(override)]),
    Either.match({ onLeft: () => false, onRight: ([b, o]) => signaturesEqual(b, o) }),
  );

const listArgs = (xs: ReadonlyArray<string>) => xs.map((x) => `{${x}}`).join(', ');
const listTags = (xs: ReadonlyArray<string>) => xs.map((x) => `<${x}>`).join(', ');

const mismatch = (what: string, expected: string, found: string) =>
  expected === ''
    ? `This text takes no ${what}; remove ${found}.`
    : `${what[0].toUpperCase()}${what.slice(1)} must be exactly ${expected}; found ${found || 'none'}.`;

// Parser error kinds (formatjs ErrorKind names) that mean a stray or unclosed brace.
const BRACE_ERROR_RE = /BRACE|ARGUMENT|EMPTY_ARGUMENT/;

const parseErrorText = (kind: string) =>
  BRACE_ERROR_RE.test(kind)
    ? `The text has an unclosed or stray "{" or "}" (${kind}). To show a brace as text, quote it: '{'.`
    : `The text is not a valid message (${kind}).`;

/** Every reason the editor (and the service) refuse `value` as an override of `base`; empty when it is fine. */
export const validateOverrideValue = (value: string, base: string): ReadonlyArray<string> => {
  if (value.trim().length === 0) return ['The text must not be empty.'];
  const basic = [
    ...(value.length > MAX_OVERRIDE_LENGTH ? [`The text is longer than ${MAX_OVERRIDE_LENGTH} characters.`] : []),
    ...(INVISIBLE_CHARS_RE.test(value) ? ['The text contains invisible characters.'] : []),
  ];
  const proposed = messageSignature(value);
  if (Either.isLeft(proposed)) return [...basic, parseErrorText(proposed.left)];
  const original = messageSignature(base);
  if (Either.isLeft(original)) return [...basic, 'The original text cannot be parsed, so it cannot be customized.'];
  return [
    ...basic,
    ...(sameSet(proposed.right.args, original.right.args)
      ? []
      : [mismatch('placeholders', listArgs(original.right.args), listArgs(proposed.right.args))]),
    ...(sameSet(proposed.right.tags, original.right.tags)
      ? []
      : [mismatch('tags', listTags(original.right.tags), listTags(proposed.right.tags))]),
  ];
};
